import { Upload, UploadProps, Button } from "antd";
import { useState, useRef } from "react";
import fileUpload from "../../assets/icons/fileUpload.svg";
import { Image } from "react-bootstrap";
import AttachmentIcon from "../../assets/icons/attachment.svg";
import DeleteIcon from "../../assets/icons/delete_2.svg";
import { useFileUploadMutation } from "../../service/apiSlices/dashboard";
import "./ModelFileUpload.css";
import { useTranslation } from "react-i18next";
import { showErrorToast, showSuccessToast } from "../../utilities/toastUtils";
import { validateModelFile } from "../../utilities/utils";

const { Dragger } = Upload;

const ModelFileUpload = ({
  hint,
  params = {},
  onSuccess,
  onError,
  uploadOnSelect = false,
  clearFileUpload
}: {
  hint: string;
  params?: any;
  onSuccess: (data: any) => void;
  onError: (error: any) => void;
  uploadOnSelect?: boolean;
  clearFileUpload?: boolean;
}) => {
  const { t } = useTranslation("common");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadFile] = useFileUploadMutation();
  const uploadRef = useRef<any>(null);

  const handleFileUpload = async (file: File) => {
    if (!validateModelFile(file, true)) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    Object.keys(params).forEach((paramKey) =>
      formData.append(paramKey, params[paramKey])
    );

    try {
      uploadFile(formData).unwrap().then((response) => {
        onSuccess(response);
        showSuccessToast(
          response.message || `${file.name} file uploaded successfully.`
        );
        setSelectedFile(file);
      })
    } catch (error) {
      onError(error);
      showErrorToast(`${file.name} file upload failed.`);
    }
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    accept: ".csv, .xlsx, .xls",
    customRequest: async (options) => {
      const { file, onSuccess, onError }: any = options;

      try {

        if (!validateModelFile(file, true)) {
          onError(new Error('File validation failed'));
          return;
        }

        if (uploadOnSelect) {
          await handleFileUpload(file);
          onSuccess("File uploaded successfully.");
        } else {
          setSelectedFile(file);
          onSuccess("File selected, ready to upload.");
        }
      } catch (error) {
        onError(error);
      }
    },
    onChange(info) {
      const file = info.file.originFileObj as File;
      if (file && validateModelFile(file, false)) {
        setSelectedFile(file);
      }
    },
  };

  const handleManualUpload = async () => {
    if (selectedFile) {
      await handleFileUpload(selectedFile);
    } else {
      showErrorToast("Please select a file first.");
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
  };

  return (
    <div>
      <Dragger {...props} ref={uploadRef} className="file-upload-dragger">
        {selectedFile ? (
          <div className="p-0">
            <div className="d-flex align-items-center justify-content-center gap-2">
              <Image src={AttachmentIcon} width={14} />
              <p className="mb-0 file-name text-truncate" style={{ maxWidth: '130px' }} title={selectedFile.name}>
                {selectedFile.name}
              </p>
            </div>
            <div className="d-flex justify-content-center gap-2">
              <Button
                size="middle"
                className="remove-btn p-0 border-0 shadow-none bg-transparent"
                icon={<Image src={DeleteIcon} width={18} />}
                onClick={handleRemoveFile}
              />
            </div>
          </div>
        ) : (
          <>
            <p className="ant-upload-drag-icon">
              <img src={fileUpload} alt="Upload" />
            </p>
            <p className="ant-upload-text">
              {t("uploadHelperText")}
            </p>
          </>
        )}
      </Dragger>
      <div>
        <p className="helper-text-upload mt-1">{t('csvUploadHelpText')}</p>
      </div>
      <Button
        className="upload-btn-model"
        size="large"
        onClick={handleManualUpload}
        title="Select file to enable upload"
      >
        {t('buttons.upload')}
      </Button>
    </div>
  );
};

export default ModelFileUpload;